// MUST stay the first import -- see bootStorageFallback.js.
import '@/bootStorageFallback'
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from '@/App'
import store from '@/store'
import router from '@/router'
import axiosInstance from '@/axios'
import { installStorefrontNavCacheLifecycle } from '@/composables/storefrontNavCacheLifecycle'
import { installAuthResponseInterceptor } from '@/interceptors/authResponseInterceptor'
import { installImpersonationSeatSync } from '@/utils/impersonationSeatSync'
import { recoverAnonymousStorefrontSession } from '@/router/anonymousStorefrontMint'
import { createVuetify } from 'vuetify'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'
import { md, aliases as mdAliases } from 'vuetify/iconsets/md'
import { mdi } from 'vuetify/iconsets/mdi'
import VueScrollTo from 'vue-scrollto'
import VueLazyload from 'vue-lazyload'
import XLSXStyle from 'xlsx-js-style'
import Youtube from '@/components/Youtube.vue'

import 'vuetify/styles'
import '@mdi/font/css/materialdesignicons.css'
import './style.css'
// ThreadFlow design system tokens (Claude Design redesign experiment)
import './design-system/colors_and_type.css'

import * as Sentry from '@sentry/browser'
import { beforeSend as sentryBeforeSend, beforeBreadcrumb as sentryBeforeBreadcrumb } from '@/utils/sentryScrub'

if (process.env.FRONTEND_SENTRY_DSN) {
  Sentry.init({
    environment: process.env.SENTRY_ENVIRONMENT,
    dsn: process.env.FRONTEND_SENTRY_DSN,
    // The storefront resume code rides in the URL fragment (#r=...) and the
    // Net Checkout PIN in a PUT body; never let a captured event carry either
    // off the device (utils/sentryScrub.js composes both scrubs).
    beforeSend: sentryBeforeSend,
    beforeBreadcrumb: sentryBeforeBreadcrumb
  })
}

// Catch errors from the API and pop toaster error messages when appropriate.
// Auth resilience: a 401 is retried ONCE (re-sending the stored token) before
// logging out, so a transient 401 during a backend/API-container restart does
// not wipe a still-valid session. On a genuine (retry-also-401) logout we
// redirect to the login page from any URL. The `window._swappingToken`
// suppression (#448) and the non-401 behavior are preserved. Logic lives in
// the extracted factory so it can be unit-tested without mounting the app.
installAuthResponseInterceptor({
  axiosInstance,
  store,
  router,
  // An anonymous storefront visitor whose session dies mid-visit is re-minted
  // in place, never sent to /login (Jake, 2026-10-01).
  recoverAnonymousSession: ({ slug }) =>
    recoverAnonymousStorefrontSession({ axiosInstance, store, slug }),
})

installStorefrontNavCacheLifecycle(store)

// The impersonation seat is stamped on the server-side token every tab of a
// login shares: when another tab changes it, reload this one into its role
// landing so its banner and capabilities match the token again.
installImpersonationSeatSync()

// Vuetify 3 configuration
const vuetify = createVuetify({
  components,
  directives,
  icons: {
    defaultSet: 'md',
    aliases: mdAliases,
    sets: { md, mdi }
  },
  theme: {
    themes: {
      light: {
        colors: {
          primary: '#119ae9',
          secondary: '#133752',
          surfaceLight: '#f5f8fa',
          surfaceDark: '#e8eff5'
        }
      }
    }
  },
  defaults: {
    // Match Vuetify 1.5 underlined style + comfortable density (Visual Audit P1-7 #285, P1-3 #281)
    VTextField: { variant: 'underlined', density: 'comfortable' },
    VSelect: { variant: 'underlined', density: 'comfortable' },
    VCombobox: { variant: 'underlined', density: 'comfortable' },
    VAutocomplete: { variant: 'underlined', density: 'comfortable' },
    VTextarea: { variant: 'underlined' },
    // ThreadFlow primary blue for toggle color (rebrand from OW red; Visual Audit P2-8 #296)
    VSwitch: { color: 'primary' },
  }
})

const app = createApp(App)

app.component('youtube', Youtube)
app.use(VueLazyload, {
  lazyComponent: true
})
app.use(VueScrollTo)
app.use(vuetify)
app.use(router)
app.use(store)
app.use(createPinia())

// Global properties
app.config.globalProperties.$axios = axiosInstance

// Global filter replacement (Vue 3 removed filters)
app.config.globalProperties.$filters = {
  capitalize(value) {
    if (!value) return ''
    value = value.toString()
    return value.charAt(0).toUpperCase() + value.slice(1)
  }
}

// Global unhandled promise rejection handler
window.addEventListener('unhandledrejection', event => {
  store.dispatch('raiseError', event.reason)
})

app.mount('#app')
